import { Injectable, inject, signal, effect, computed, DestroyRef } from '@angular/core';

import { ViewerCoreService } from './viewer-core.service.js';
import { onViewerEvents } from './event-dispatcher-bridge';
import { CameraMode } from '../shared/defines.js';

/**
 * Exposes Scene state as Angular signals.
 * Collections are updated via Scene events (measurement_added, volume_added, etc.).
 */
@Injectable({ providedIn: 'root' })
export class SceneStateService {
  private core = inject(ViewerCoreService);
  private destroyRef = inject(DestroyRef);

  readonly measurements = signal<any[]>([]);
  readonly profiles = signal<any[]>([]);
  readonly volumes = signal<any[]>([]);
  readonly polygonClipVolumes = signal<any[]>([]);
  readonly pointclouds = signal<any[]>([]);
  readonly annotations = signal<any | null>(null);
  readonly cameraMode = signal<number>(CameraMode.PERSPECTIVE);
  readonly activeCamera = signal<any | null>(null);
  readonly view = signal<any | null>(null);

  readonly measurementCount = computed(() => this.measurements().length);
  readonly profileCount = computed(() => this.profiles().length);
  readonly volumeCount = computed(() => this.volumes().length);
  readonly pointcloudCount = computed(() => this.pointclouds().length);
  readonly hasPointclouds = computed(() => this.pointclouds().length > 0);

  constructor() {
    effect(() => {
      const viewer = this.core.viewer();
      if (!viewer?.scene) return;

      const scene = viewer.scene;
      this._syncFromScene(scene);

      onViewerEvents(
        scene,
        {
          measurement_added: () => this._pullMeasurements(scene),
          measurement_removed: () => this._pullMeasurements(scene),
          profile_added: () => this._pullProfiles(scene),
          profile_removed: () => this._pullProfiles(scene),
          volume_added: () => this._pullVolumes(scene),
          volume_removed: () => this._pullVolumes(scene),
          polygon_clip_volume_added: () => this._pullPolygonClipVolumes(scene),
          polygon_clip_volume_removed: () => this._pullPolygonClipVolumes(scene),
          pointcloud_added: () => this._pullPointclouds(scene),
        },
        this.destroyRef,
      );
    });
  }

  // ─── Actions ──────────────────────────────────────────────────────────────

  addMeasurement(measurement: any): void {
    const viewer = this.core.viewer();
    viewer?.scene?.addMeasurement?.(measurement);
  }

  removeMeasurement(measurement: any): void {
    const viewer = this.core.viewer();
    viewer?.scene?.removeMeasurement?.(measurement);
  }

  addProfile(profile: any): void {
    const viewer = this.core.viewer();
    viewer?.scene?.addProfile?.(profile);
  }

  removeProfile(profile: any): void {
    const viewer = this.core.viewer();
    viewer?.scene?.removeProfile?.(profile);
  }

  addVolume(volume: any): void {
    const viewer = this.core.viewer();
    viewer?.scene?.addVolume?.(volume);
  }

  removeVolume(volume: any): void {
    const viewer = this.core.viewer();
    viewer?.scene?.removeVolume?.(volume);
  }

  removeAllMeasurements(): void {
    const viewer = this.core.viewer();
    viewer?.scene?.removeAllMeasurements?.();
  }

  removeAllClipVolumes(): void {
    const viewer = this.core.viewer();
    viewer?.scene?.removeAllClipVolumes?.();
  }

  getActiveCamera(): any {
    const viewer = this.core.viewer();
    return viewer?.scene?.getActiveCamera?.() ?? null;
  }

  getBoundingBox(): any {
    const viewer = this.core.viewer();
    return viewer?.scene?.getBoundingBox?.() ?? null;
  }

  // ─── Private sync ─────────────────────────────────────────────────────────

  private _syncFromScene(scene: any): void {
    this._pullMeasurements(scene);
    this._pullProfiles(scene);
    this._pullVolumes(scene);
    this._pullPolygonClipVolumes(scene);
    this._pullPointclouds(scene);
    this.annotations.set(scene.annotations ?? null);
    this.cameraMode.set(scene.cameraMode ?? CameraMode.PERSPECTIVE);
    this.activeCamera.set(scene.getActiveCamera?.() ?? null);
    this.view.set(scene.view ?? null);
  }

  private _pullMeasurements(scene: any): void {
    this.measurements.set([...(scene.measurements ?? [])]);
  }

  private _pullProfiles(scene: any): void {
    this.profiles.set([...(scene.profiles ?? [])]);
  }

  private _pullVolumes(scene: any): void {
    this.volumes.set([...(scene.volumes ?? [])]);
  }

  private _pullPolygonClipVolumes(scene: any): void {
    this.polygonClipVolumes.set([...(scene.polygonClipVolumes ?? [])]);
  }

  private _pullPointclouds(scene: any): void {
    this.pointclouds.set([...(scene.pointclouds ?? [])]);
  }
}
